import { some, times } from "lodash-es";
import {
  useEffect,
  useEffectEvent,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  DrawerContent,
  DrawerFooter,
  DrawerTopBar,
  SwipeableDrawer,
} from "@/components/Drawer";
import { ErrorPanel } from "@/components/Errors/ErrorPanel";
import { NoFilteringResults } from "@/components/Errors/NoFilteringResults";
import { IconHotelBedWithMagnifier } from "@/components/Icons";
import { SearchResultsWhatsAppBanner } from "@/components/WhatsApp/WhatsAppBanners";
import { WhatsAppButton } from "@/components/WhatsApp/WhatsAppButton";
import {
  HotelCard,
  HotelCardSkeleton,
} from "@/features/hotels/components/hotels-list/HotelCard";
import { HotelListHeader } from "@/features/hotels/components/hotels-list/HotelListHeader";
import { HotelsFilters } from "@/features/hotels/components/hotels-list/HotelsFilters";
import { HotelsSortDrawer } from "@/features/hotels/components/hotels-list/HotelsSortDrawer";
import { PriceFilterDrawer } from "@/features/hotels/components/hotels-list/PriceFilterDrawer";
import { SearchByNameDrawer } from "@/features/hotels/components/hotels-list/SearchByNameDrawer";
import { StarRatingFilterDrawer } from "@/features/hotels/components/hotels-list/StarRatingFilterDrawer";
import { HotelsListDrawerComponent } from "@/features/hotels/components/hotels-list/types";
import { HotelSearchForm } from "@/features/hotels/components/HotelSearchForm/HotelSearchForm";
import { useHotelFiltersContext } from "@/features/hotels/contexts/HotelFiltersContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { useHotelSortContext } from "@/features/hotels/contexts/HotelSortContext";
import { useHotelSearch } from "@/features/hotels/hooks/useHotelSearch";
import { useHotelSearchForm } from "@/features/hotels/hooks/useHotelSearchForm";
import { useHotelSearchResults } from "@/features/hotels/hooks/useHotelSearchResults";
import { useOnHotelOfferClick } from "@/features/hotels/hooks/useOnHotelOfferClick";
import {
  BOARD_BASIS_OPTIONS,
  HOTEL_CLASSES,
} from "@/features/hotels/utils/constants";
import { getNumberOfBookingNights } from "@/features/hotels/utils/getNumberOfBookingNights";
import type { HotelSearchFormValues } from "@/features/hotels/utils/hotelsValidation";
import { TripBanner } from "@/features/trips/components/TripBanner";
import {
  rudderAnalytics,
  HotelFiltersTrackingEvents,
  HotelTrackingEvents,
  TripTrackingEvents,
  type HotelFiltersBarTrackingEvent,
} from "@/lib/rudderstack";
import { getDateString } from "@/utils/dates";

const FILTER_BAR_EVENT_MAP: Partial<
  Record<HotelsListDrawerComponent, HotelFiltersBarTrackingEvent>
> = {
  [HotelsListDrawerComponent.SEARCH_BY_NAME]:
    HotelFiltersTrackingEvents.USER_CLICKED_ON_FILTER_SEARCHBYNAME,
  [HotelsListDrawerComponent.PRICE]:
    HotelFiltersTrackingEvents.USER_CLICKED_ON_FILTER_PRICE,
  [HotelsListDrawerComponent.HOTEL_CLASS]:
    HotelFiltersTrackingEvents.USER_CLICKED_ON_FILTER_STAR,
  [HotelsListDrawerComponent.ALL_FILTERS]:
    HotelFiltersTrackingEvents.USER_CLICKED_ON_FILTER_ALL,
  [HotelsListDrawerComponent.SORT]:
    HotelFiltersTrackingEvents.USER_CLICKED_ON_SORT_FILTER,
};

function HotelsList() {
  const intl = useIntl();
  const [drawerComponent, setDrawerComponent] =
    useState<HotelsListDrawerComponent | null>(null);

  const { sortValue, updateSortValue } = useHotelSortContext();
  const { defaultFilters, filters, activeFilters, updateFilters } =
    useHotelFiltersContext();
  const { searchData } = useHotelSearchDataContext();
  const {
    searchResultKey,
    searchResultToken,
    progress,
    progressData,
    isFetching,
  } = useHotelSearch();
  const { handleSubmit } = useHotelSearchForm();

  const isProgressComplete = progress === 100;
  const isNoOffersFound =
    isProgressComplete && (progressData?.countOffers ?? 0) === 0;
  const areFiltersDirty = some(activeFilters, Boolean);

  // remember latest refs of functions to be able to omit them
  // in effect deps array
  const savedUpdateFilters = useRef(updateFilters);
  useEffect(() => {
    savedUpdateFilters.current = updateFilters;
  }, [updateFilters]);

  // using useLayoutEffect to avoid UI flickering
  useLayoutEffect(() => {
    if (!filters && isProgressComplete && progressData) {
      savedUpdateFilters.current({
        hotelName: "",
        priceRange: [progressData.minPrice, progressData.maxPrice],
        minPrice: progressData.minPrice,
        maxPrice: progressData.maxPrice,
        boardBasis: progressData.boardBasis || BOARD_BASIS_OPTIONS,
        starRating: progressData.stars || HOTEL_CLASSES,
      });
    }
  }, [
    filters,
    isProgressComplete,
    progressData,
    searchData?.dates.checkIn,
    searchData?.dates.checkOut,
  ]);

  const {
    data: searchResultData,
    isLoading: isLoadingData,
    isFetching: isFetchingData,
    isError,
    hasReachedEnd,
    isEmpty: isSearchResultEmpty,
    fetchNextPage,
  } = useHotelSearchResults({
    searchResultKey,
    searchResultToken,
    filters,
    shouldFetch: isProgressComplete && !isNoOffersFound,
    sortValue,
    totalOffers: progressData?.countOffers,
    destinationCity: searchData?.destination?.name,
    destinationCountry: searchData?.destination?.countryName,
  });

  const isEmpty = isNoOffersFound || isSearchResultEmpty;

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingData,
    hasNextPage: !hasReachedEnd,
    onLoadMore: () => {
      rudderAnalytics.track(HotelTrackingEvents.USER_SEARCHED_FOR_MORE_OFFERS);
      fetchNextPage();
    },
    disabled: isError,
    rootMargin: "0px 0px 40px 0px",
  });

  const isLoading = isFetching || isLoadingData;

  const sendNoHotelResultsFoundEvent = useEffectEvent(() => {
    if (areFiltersDirty || !searchData?.destination) return;
    const travellersCount = searchData.rooms.reduce(
      (acc, v) => acc + v.adults + v.children,
      0,
    );

    rudderAnalytics.track(HotelTrackingEvents.NO_HOTEL_RESULTS_FOUND, {
      destinationId: searchData.destination.id,
      destinationName: searchData.destination.name,
      dateFrom: getDateString(searchData.dates.checkIn),
      dateTo: getDateString(searchData.dates.checkOut),
      travellers: travellersCount,
    });
  });

  const handleSearchSubmit = (values: HotelSearchFormValues) => {
    handleSubmit(values);
    setDrawerComponent(null);
  };

  const handleOfferClick = useOnHotelOfferClick();

  const bookingNights = getNumberOfBookingNights({
    checkIn: searchData?.dates.checkIn,
    checkOut: searchData?.dates.checkOut,
  });

  useEffect(() => {
    if (isEmpty) {
      sendNoHotelResultsFoundEvent();
    }
  }, [isEmpty]);

  return (
    <>
      <div className="flex grow flex-col px-4">
        <HotelListHeader
          onFilterButtonClick={(drawerComponent) => {
            const event = FILTER_BAR_EVENT_MAP[drawerComponent];
            if (
              event === HotelFiltersTrackingEvents.USER_CLICKED_ON_SORT_FILTER
            ) {
              rudderAnalytics.track(event, {
                current_search_query: filters?.hotelName || "",
                current_sort_type: sortValue.field,
              });
            } else if (event) {
              rudderAnalytics.track(event, {
                current_search_query: filters?.hotelName || "",
              });
            }
            setDrawerComponent(drawerComponent);
          }}
          selectedSortValue={sortValue}
          progress={isLoading && progress < 100 ? progress : undefined}
          activeFilters={activeFilters}
          onSearchDrawerOpen={() =>
            setDrawerComponent(HotelsListDrawerComponent.SEARCH)
          }
        />

        {!isLoading && isEmpty ? (
          <div className="mt-12 flex grow flex-col items-center justify-between gap-8">
            {areFiltersDirty ? (
              <NoFilteringResults
                scope="hotels"
                title={intl.formatMessage({
                  defaultMessage: "No hotels match your filters",
                  description:
                    "Heading shown when active filters return zero hotel results",
                })}
                subtitle={intl.formatMessage({
                  defaultMessage:
                    "Try removing some filters or adjusting your preferences to see more options",
                  description:
                    "Description shown when active filters return zero hotel results",
                })}
                onClearFilterClick={() => {
                  updateFilters(defaultFilters);
                }}
              />
            ) : (
              <ErrorPanel
                icon={IconHotelBedWithMagnifier}
                primaryText={intl.formatMessage({
                  defaultMessage: "No hotels found",
                  description:
                    "Heading shown when the hotel search returns no results",
                })}
                secondaryText={intl.formatMessage({
                  defaultMessage:
                    "We couldn't find any hotel for you. Check your filters or try different dates.",
                  description:
                    "Description shown when no hotels are found, prompting user to adjust search criteria",
                })}
              >
                <Button
                  onClick={(e) => {
                    setDrawerComponent(HotelsListDrawerComponent.SEARCH);
                    e.currentTarget.blur();
                  }}
                  variant="outlined"
                  borderRoundness="medium"
                  color="primary"
                  size="small"
                >
                  {intl.formatMessage({
                    defaultMessage: "Edit search",
                    description:
                      "Button to edit / modify the current search parameters",
                  })}
                </Button>
              </ErrorPanel>
            )}

            <SearchResultsWhatsAppBanner />
          </div>
        ) : (
          <div className="w-full">
            {isLoading ? (
              <ul className="space-y-2">
                {times(3, (index) => (
                  <li key={index}>
                    <HotelCardSkeleton />
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="space-y-2">
                {searchResultData?.pages
                  ?.flatMap((offers) => offers)
                  ?.map((offer, index) => {
                    if (offer.type === "PACKAGE_BANNER") {
                      const eventData = {
                        package_id: offer.packageId,
                        package_name: offer.packageName,
                        banner_name: offer.bannerTitle,
                        banner_url: offer.imageUrl,
                        list: "hotel",
                        city_name: offer.cityName,
                        city_id: offer.cityId,
                        position: offer.position,
                      } as const;

                      return (
                        <li key={`banner-${offer.packageId}-${index}`}>
                          <TripBanner
                            tripId={offer.packageId}
                            src={offer.imageUrl}
                            title={offer.bannerTitle}
                            onViewed={() => {
                              rudderAnalytics.track(
                                TripTrackingEvents.TRIP_PROMO_BANNER_VIEWED,
                                eventData,
                              );
                            }}
                            onClicked={() => {
                              rudderAnalytics.track(
                                TripTrackingEvents.TRIP_PROMO_BANNER_CLICKED,
                                eventData,
                              );
                            }}
                          />
                        </li>
                      );
                    }

                    return (
                      <li key={offer.id}>
                        <HotelCard
                          onClick={() => handleOfferClick(offer)}
                          hotel={offer}
                          bookingNights={bookingNights}
                        />
                      </li>
                    );
                  })}
                {isFetching || !hasReachedEnd
                  ? times(3, (index) => (
                      <li
                        key={index}
                        ref={index === 0 ? sentryRef : undefined}
                        aria-hidden={!isFetching}
                      >
                        <HotelCardSkeleton />
                      </li>
                    ))
                  : null}
              </ul>
            )}
          </div>
        )}

        <HotelsSortDrawer
          open={drawerComponent === HotelsListDrawerComponent.SORT}
          value={sortValue}
          onSubmit={(newSortValue) => {
            rudderAnalytics.track(
              HotelFiltersTrackingEvents.USER_CLICKED_ON_SORT_OPTION,
              {
                selected_sort_type: newSortValue.field,
                previous_sort_type: sortValue.field,
              },
            );
            updateSortValue(newSortValue);
            setDrawerComponent(null);
          }}
          onClose={() => setDrawerComponent(null)}
        />
        <StarRatingFilterDrawer
          open={drawerComponent === HotelsListDrawerComponent.HOTEL_CLASS}
          onCloseClick={() => setDrawerComponent(null)}
          defaultFilter={defaultFilters?.starRating}
          value={filters?.starRating}
          onSubmit={(newValue) =>
            updateFilters((prev) =>
              prev ? { ...prev, starRating: newValue } : null,
            )
          }
        />
        <SearchByNameDrawer
          open={drawerComponent === HotelsListDrawerComponent.SEARCH_BY_NAME}
          value={filters?.hotelName || ""}
          onClose={() => setDrawerComponent(null)}
          onSubmit={(query) => {
            updateFilters((prev) =>
              prev ? { ...prev, hotelName: query } : null,
            );
            setDrawerComponent(null);
          }}
        />

        {defaultFilters && filters ? (
          <SwipeableDrawer
            open={drawerComponent === HotelsListDrawerComponent.ALL_FILTERS}
            onClose={() => setDrawerComponent(null)}
            hasFullHeight
          >
            <HotelsFilters
              defaultFilters={defaultFilters}
              filters={filters}
              sortValue={sortValue}
              bookingNights={bookingNights}
              onFiltersChange={(newFilters, newSortValue) => {
                updateFilters((prevFilters) =>
                  prevFilters
                    ? {
                        ...prevFilters,
                        ...newFilters,
                      }
                    : null,
                );
                updateSortValue(newSortValue);
                setDrawerComponent(null);
              }}
              onClose={() => {
                setDrawerComponent(null);
              }}
            />
          </SwipeableDrawer>
        ) : null}

        {defaultFilters && filters ? (
          <PriceFilterDrawer
            open={drawerComponent === HotelsListDrawerComponent.PRICE}
            defaultFilters={defaultFilters}
            filters={filters}
            bookingNights={bookingNights}
            onApply={(newPriceRange) => {
              updateFilters((prevFilters) =>
                prevFilters
                  ? {
                      ...prevFilters,
                      priceRange: newPriceRange,
                    }
                  : null,
              );
            }}
            onClose={() => setDrawerComponent(null)}
          />
        ) : null}

        <HotelSearchFormDrawer
          open={drawerComponent === HotelsListDrawerComponent.SEARCH}
          onClose={() => setDrawerComponent(null)}
          onSubmit={handleSearchSubmit}
          searchData={searchData}
        />
      </div>
      {!isLoading && isEmpty ? null : <WhatsAppButton />}
    </>
  );
}

export default HotelsList;

type HotelSearchFormDrawerProps = {
  open: boolean;
  onClose: () => void;
  searchData: HotelSearchFormValues | null;
  onSubmit: (values: HotelSearchFormValues) => void;
};

function HotelSearchFormDrawer({
  open,
  onClose,
  onSubmit,
  searchData,
}: HotelSearchFormDrawerProps) {
  const intl = useIntl();
  const formId = useId();

  return (
    <SwipeableDrawer hasFullHeight open={open} onClose={onClose}>
      <DrawerTopBar
        onCloseClick={onClose}
        title={intl.formatMessage({
          defaultMessage: "Edit search",
          description: "Title of drawer with hotel search form",
        })}
      />

      <DrawerContent>
        <HotelSearchForm
          formId={formId}
          inputsWrappedInCard
          analyticsSource="sticky"
          initialValues={searchData}
          showErrorsInline
          onSubmit={onSubmit}
        />
      </DrawerContent>

      <DrawerFooter>
        <Button fullWidth variant="contained" type="submit" form={formId}>
          {intl.formatMessage({
            defaultMessage: "Search hotel",
            description: "Submit/search button label for the hotel search form",
          })}
        </Button>
      </DrawerFooter>
    </SwipeableDrawer>
  );
}
